import { z } from "zod";
import { responseSchema } from "../http.js";
import { currencySchema, priceCentsSchema } from "./product.js";
/**
 * ═══════════════════════════════════════════════════════════
 * Cupones de descuento
 *
 * Réplica de `shop/admin/coupons` de producción (Anahí, 27-ago): el
 * super-admin crea cupones y el cliente los escribe AL FINAL del
 * checkout. El descuento lo calcula el SERVIDOR al crear la orden — el
 * navegador solo manda el código, por la misma regla que los precios:
 * si el importe del descuento viajara desde el cliente, cualquiera se
 * inventaría el suyo.
 *
 * ── Dos clases de cupón ────────────────────────────────────
 * `percent` (10% de descuento) vale en cualquier moneda. `fixed`
 * ($200.00 menos) lleva SU moneda y solo aplica a pedidos en ella:
 * restar 200 pesos de un pedido en dólares sería inventar un tipo de
 * cambio que nadie fijó.
 * ═══════════════════════════════════════════════════════════
 */
/**
 * El código que se teclea: mayúsculas, números y guiones, 3–24. Se
 * normaliza a MAYÚSCULAS en el borde — "bienvenida10" y "BIENVENIDA10"
 * son el mismo cupón, porque así se dicta por teléfono.
 */
export const couponCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9-]{3,24}$/, "Solo letras, números y guiones (3 a 24)");
export const COUPON_KINDS = ["percent", "fixed"];
export const couponKindSchema = z.enum(COUPON_KINDS);
/**
 * Lo que entra al crear un cupón. `percent`/`amountCents` según la
 * clase; el `superRefine` obliga a que la clase y su valor vengan
 * juntos, y a que un fijo diga su moneda.
 */
export const couponInputSchema = z
    .object({
    code: couponCodeSchema,
    kind: couponKindSchema,
    /** 1–100, solo para `percent`. */
    percent: z.number().int().min(1, "Al menos 1%").max(100, "Máximo 100%").optional(),
    /** El monto a restar, solo para `fixed`. */
    amountCents: priceCentsSchema.optional(),
    /** La moneda del monto fijo. Obligatoria para `fixed`. */
    currency: currencySchema.optional(),
    /** Cuántas veces puede usarse en total. Sin él, sin límite. */
    maxRedemptions: z.number().int().min(1).optional(),
    /** YYYY-MM-DD, inclusivo. Sin él, no vence. */
    expiresAt: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, "La vigencia es una fecha AAAA-MM-DD")
        .optional(),
    active: z.boolean().default(true),
})
    .superRefine((value, ctx) => {
    if (value.kind === "percent" && value.percent === undefined) {
        ctx.addIssue({ code: "custom", path: ["percent"], message: "Escribe el porcentaje" });
    }
    if (value.kind === "fixed") {
        if (value.amountCents === undefined) {
            ctx.addIssue({ code: "custom", path: ["amountCents"], message: "Escribe el monto" });
        }
        if (value.currency === undefined) {
            ctx.addIssue({ code: "custom", path: ["currency"], message: "Elige la moneda del monto" });
        }
    }
});
/** Lo único editable después: apagarlo/encenderlo. El resto es historia
 * de un código ya repartido — cambiarle el descuento bajo los pies de
 * quien ya lo tiene sería otra promesa con el mismo nombre. */
export const couponUpdateSchema = z.object({ active: z.boolean() });
export const couponSchema = z.object({
    id: z.string(),
    code: couponCodeSchema,
    kind: couponKindSchema,
    percent: z.number().int().optional(),
    amountCents: priceCentsSchema.optional(),
    currency: currencySchema.optional(),
    maxRedemptions: z.number().int().optional(),
    expiresAt: z.string().optional(),
    active: z.boolean(),
    /** Cuántas órdenes lo han usado. Lo cuenta el servidor. */
    redemptionCount: z.number().int().min(0),
    createdAt: z.string(),
});
/**
 * Lo que el CHECKOUT necesita saber de un cupón válido para pintar el
 * estimado del descuento. Sin usos ni vigencias: eso es del panel.
 */
export const couponPublicSchema = couponSchema.pick({
    code: true,
    kind: true,
    percent: true,
    amountCents: true,
    currency: true,
});
/** "¿Este código vale para MI pedido?" — código y la moneda del carrito. */
export const couponValidateRequestSchema = z.object({
    code: couponCodeSchema,
    currency: currencySchema,
});
/**
 * El descuento de un cupón sobre un subtotal, en centavos. UNA función
 * para el servidor (el cobro real) y el frontend (el estimado en
 * pantalla): dos implementaciones divergirían en el redondeo.
 * Nunca más que el subtotal: un cupón no pone dinero a favor.
 */
export function couponDiscountCents(coupon, subtotalCents) {
    const raw = coupon.kind === "percent"
        ? Math.round((subtotalCents * (coupon.percent ?? 0)) / 100)
        : (coupon.amountCents ?? 0);
    return Math.max(0, Math.min(raw, subtotalCents));
}
/**
 * El listado del panel filtra y pagina EN EL SERVIDOR (Anahí, 27-ago):
 * búsqueda por código, estado encendido/apagado, y solo la página que
 * se ve.
 */
export const couponListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(24),
    query: z.string().trim().max(60).optional(),
    status: z.enum(["active", "inactive"]).optional(),
});
export const couponListResponseSchema = responseSchema(z.object({
    items: z.array(couponSchema),
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    perPage: z.number().int().min(1),
}));
export const couponResponseSchema = responseSchema(z.object({ coupon: couponSchema }));
export const couponValidateResponseSchema = responseSchema(z.object({ coupon: couponPublicSchema }));
export const COUPON_ROUTES = {
    adminList: "/api/admin/coupons",
    adminCreate: "/api/admin/coupons",
    adminOne: (id) => `/api/admin/coupons/${id}`,
    /** Con sesión (el checkout la exige), fuera de `/admin`. */
    validate: "/api/coupons/validate",
};
//# sourceMappingURL=coupon.js.map